import { Box, Heading, NumberInput, NumberInputField, Tooltip } from '@chakra-ui/react';
import { FC, useState } from 'react';
import { LuInfo } from 'react-icons/lu';
import { resolveAutoFitLineHeight } from '@adflow/ad';
import { useStore } from '../../../hooks/useStore';
import { shallow } from 'zustand/shallow';
import { AdElementText } from '@adflow/types';

type Props = {
  selectedElement: AdElementText;
};

const MIN_RELATIVE_LINE_HEIGHT = 0.5;
const MAX_RELATIVE_LINE_HEIGHT = 3;

const LineHeightInput: FC<Props> = ({ selectedElement }) => {
  const store = useStore(
    (state) => ({
      updateElementData: state.updateElementData,
    }),
    shallow,
  );
  // The raw text while the field is focused, so partial input is not replaced on each keystroke.
  const [draft, setDraft] = useState<string | null>(null);

  const dynamicFont = selectedElement.data.dynamicFont;

  const handleLineHeight = (lineHeight: string) => {
    setDraft(lineHeight);
    const lineHeightNumber = parseFloat(lineHeight);
    if (!Number.isFinite(lineHeightNumber)) {
      return;
    }
    const isValidHeight = lineHeightNumber > 1;
    store.updateElementData(
      selectedElement.id,
      {
        ...selectedElement.data,
        style: {
          ...selectedElement.data.style,
          lineHeight: isValidHeight ? lineHeightNumber : 1,
        },
      },
      { mergeKey: `line-height:${selectedElement.id}` },
    );
  };

  // Save only complete, in-range values while typing; blur does the one-time clamp,
  // mirroring Chakra's clampValueOnBlur. Clamping on each keystroke would jump e.g.
  // the first keystroke of "0.8" ("0") to 0.5 before the user can finish typing.
  const handleRelativeLineHeight = (raw: string) => {
    if (dynamicFont.enabled !== true) {
      return;
    }
    setDraft(raw);
    const parsed = parseFloat(raw);
    if (
      Number.isFinite(parsed) &&
      parsed >= MIN_RELATIVE_LINE_HEIGHT &&
      parsed <= MAX_RELATIVE_LINE_HEIGHT
    ) {
      store.updateElementData(
        selectedElement.id,
        {
          ...selectedElement.data,
          dynamicFont: {
            ...dynamicFont,
            lineHeight: parsed,
          },
        },
        { mergeKey: `relative-line-height:${selectedElement.id}` },
      );
    }
  };

  const handleRelativeBlur = () => {
    if (dynamicFont.enabled === true && draft !== null) {
      const parsed = parseFloat(draft);
      if (Number.isFinite(parsed)) {
        const clamped = Math.min(
          MAX_RELATIVE_LINE_HEIGHT,
          Math.max(MIN_RELATIVE_LINE_HEIGHT, parsed),
        );
        store.updateElementData(selectedElement.id, {
          ...selectedElement.data,
          dynamicFont: {
            ...dynamicFont,
            lineHeight: clamped,
          },
        });
      }
    }
    setDraft(null);
  };

  // Auto-fit uses a unitless line height (multiplier of the computed font size),
  // since a fixed px value is meaningless while the font size scales dynamically.
  if (dynamicFont.enabled === true) {
    // Display exactly what the renderer resolves, so legacy stored values
    // (e.g. px lengths like 45.856) show as their derived multiplier.
    const resolvedLineHeight = resolveAutoFitLineHeight(
      dynamicFont.lineHeight,
      selectedElement.data.style,
    );
    return (
      <Box>
        <Heading fontSize="md" fontWeight="semibold" display="flex" alignItems="center">
          <span style={{ whiteSpace: 'nowrap' }}>Line height</span>
          <Tooltip
            label="When auto-fit is enabled, line height automatically scales with the font size. A value of 1.2 means 120% of the font size."
            hasArrow
          >
            <span style={{ marginLeft: 4, display: 'inline-flex' }}>
              <LuInfo />
            </span>
          </Tooltip>
        </Heading>
        <NumberInput
          size="sm"
          width={20}
          variant="outline"
          mt={2}
          value={draft ?? resolvedLineHeight}
          step={0.1}
          min={MIN_RELATIVE_LINE_HEIGHT}
          max={MAX_RELATIVE_LINE_HEIGHT}
          precision={1}
          onChange={handleRelativeLineHeight}
        >
          <NumberInputField data-test-id="line-height-relative-input" onBlur={handleRelativeBlur} />
        </NumberInput>
      </Box>
    );
  }

  return (
    <Box>
      <Heading fontSize="md" fontWeight="semibold">
        Line height
      </Heading>
      <NumberInput
        value={draft ?? (selectedElement.data.style.lineHeight || '')}
        onChange={handleLineHeight}
        size="sm"
        width={20}
        variant="outline"
        mt={2}
        precision={0}
        min={1}
      >
        <NumberInputField onBlur={() => setDraft(null)} data-test-id="line-height-input" />
      </NumberInput>
    </Box>
  );
};

LineHeightInput.displayName = 'LineHeightInput';

export default LineHeightInput;
export type { Props as LineHeightInputProps };
